import React, { useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Form } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { FarmerCreateOptionsType } from '~/api/services/farmers/index'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { useDebounce } from 'use-lodash-debounce';

interface LocationProps {
  farmersOptions: FarmerCreateOptionsType
}

const Location: React.FC<LocationProps> = (props) => {
  const { farmersOptions } = props
  const { t } = useTranslation()
  const { control, setValue, formState, formState: { dirtyFields, touchedFields } } = useFormContext()

  const watchCountryField = useWatch({ control, name: 'countryId' })
  const watchStateField = useWatch({ control, name: 'stateId' })
  const watchZipField = useWatch({ control, name: 'zip' })
  const debounceZip = useDebounce(watchZipField, 300)

  const defaultCountrySelected = watchCountryField === farmersOptions?.defaultCountry

  const {
    data: states,
    isLoading: isLoadingStates,
    isFetching: isFetchingStates,
    refetch: fetchStates
  } = FarmersQuery.getStates(!defaultCountrySelected ? watchCountryField : null)

  const {
    data: zipData,
    refetch: fetchZipData
  } = FarmersQuery.getZipDistrictState(debounceZip, dirtyFields?.zip || touchedFields.zip)

  useEffect(() => {
    if (debounceZip?.length >= 4 && (dirtyFields?.zip || touchedFields.zip)) {
      fetchZipData()
    }
  }, [debounceZip, dirtyFields, touchedFields, formState])

  useEffect(() => {
    if (watchCountryField && !defaultCountrySelected) {
      fetchStates()
    }
  }, [watchCountryField])

  useEffect(() => {
    if (zipData?.stateId) {
      setValue('stateId', zipData?.stateId)
    }
    if (zipData?.countryId) {
      setValue('countryId', zipData?.countryId)
    }
    if (zipData?.id) {
      setValue('districtId', zipData?.id)
    }
  }, [zipData])

  const selectedState = !defaultCountrySelected ? states?.find(state => state.id === watchStateField) : farmersOptions?.states?.find(state => state.id === watchStateField)
  const stateOptions = !defaultCountrySelected ? states?.map(state => ({ value: state.id, text: state.label })) : farmersOptions?.states?.map(state => ({ value: state.id, text: state.label }))

  return <>
    <Form.Group style={{ alignItems: 'end' }} widths={'equal'}>
      <FormFields.Input
        label={t('Street')}
        placeholder={t('Street')}
        control={control}
        name={'streetName'}
      />
      <FormFields.Input
        label={t('Street number')}
        control={control}
        placeholder={t('Street number')}
        name={'streetNumber'}
      />
    </Form.Group>
    <Form.Group style={{ alignItems: 'end' }}>
      <FormFields.Input
        width={5}
        label={t('Zip')}
        rules={{
          required: {
            value: true,
            message: t('Please enter Zip')
          }
        }}
        placeholder={t('zip')}
        control={control}
        name={'zip'}
      />
      <FormFields.Input
        width={11}
        label={t('City')}
        control={control}
        placeholder={t('City')}
        rules={{
          required: {
            value: true,
            message: t('Please enter City')
          }
        }}
        styleLabel={{ left: '40%' }}
        name={'city'}
      />
      <FormFields.Input
        name='localDistrict'
        control={control}
        label={t('District')}
        width={5}
        placeholder={t('District')}
      />
    </Form.Group>
    <Form.Group widths={'equal'}>
      <FormFields.Select
        control={control}
        placeholder={t('Country')}
        selectOnBlur={false}
        disabled={isFetchingStates || isLoadingStates}
        search
        name='countryId'
        rules={{
          required: {
            value: true,
            message: t('Please select Country')
          }
        }}
        onChange={((e, data) => {
          setValue('stateId', '')
          setValue('agricultureDistrictId', '')
          setValue('districtId', '')
          setValue('countryId', data.value)
          setValue('administrativeDistrictId', '')
        })}
        label={t('Country')}
        options={farmersOptions?.countries.map(country => ({ value: country.id, text: country.label, flag: country.iso }))}
      />
      {stateOptions?.length > 0 && watchCountryField && stateOptions?.length > 0 && <FormFields.Select
        control={control}
        placeholder={t('State')}
        search
        loading={isFetchingStates || isLoadingStates}
        disabled={isFetchingStates || isLoadingStates}
        selectOnBlur={false}
        noResultsMessage={t('No results found....')}
        name='stateId'
        label={t('State')}
        options={stateOptions || []}
      />}
    </Form.Group>
    <Form.Group widths={'equal'}>
      {watchCountryField && selectedState?.districts?.length > 0 && <FormFields.Select
        control={control}
        placeholder={t('District / Independent city')}
        search
        loading={isFetchingStates || isLoadingStates}
        disabled={(isFetchingStates || isLoadingStates) || !selectedState}
        selectOnBlur={false}
        onChange={((e, data) => {
          setValue('agricultureDistrictId', '')
          setValue('districtId', data.value)
        })}
        upward
        noResultsMessage={t('No results found....')}
        name='districtId'
        label={t('District / Independent city')}
        options={selectedState?.districts.map(district => ({ text: `${district.name} (${district.zip})`, value: district.id })) || []}
      />}
      {watchCountryField && selectedState?.agricultureDistricts?.length > 0 && <FormFields.Select
        selectOnBlur={false}
        control={control}
        label={t('LWA / District No.')}
        upward
        disabled={(isFetchingStates || isLoadingStates) || !selectedState}
        name={'agricultureDistrictId'}
        clearable
        placeholder={t('LWA / District No.')}
        options={selectedState?.agricultureDistricts.map(agricultureDistrict => ({ text: `${agricultureDistrict.name} (${agricultureDistrict.number})`, value: agricultureDistrict.id })) || []}
      />}
      {watchCountryField && selectedState?.administrativeDistricts?.length > 0 && <FormFields.Select
        selectOnBlur={false}
        control={control}
        label={t('Government district')}
        upward
        disabled={(isFetchingStates || isLoadingStates) || !selectedState}
        name={'administrativeDistrictId'}
        clearable
        placeholder={t('Government district')}
        options={selectedState?.administrativeDistricts.map(administritativeDistrict => ({ text: administritativeDistrict.name, value: administritativeDistrict.id })) || []}
      />}
    </Form.Group>
  </>
}
export default Location